import React from 'react'
import ReactDOM from 'react-dom'

import App, { IRegionalPollMap } from './App'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { Store, createStore } from 'redux'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { PersistGate } from 'redux-persist/integration/react'

import * as serviceWorker from './serviceWorker'
import DataController, {
  BASE_ELECTION_KEY,
  BYELECTIONS_KEY,
  ELECTION_2019_KEY,
  resultForKey,
  saveResult
} from './controllers/DataController'

import {
  IReduxAction,
  IReduxState,
  generateResultKey
} from './utils/ReduxStore'
import { IPartyNumberMap } from './models/TypeHelper'
import {
  compareResultKeys,
  processResultKey
} from './controllers/ElectionResultStore'
import {
  nationalPollsToRegionalPolls,
  swingFromRegionalPolls
} from './utils/StatsUtils'
import Party from './models/Party'
import Region from './models/Region'

function isNationalPoll(obj: any): obj is IPartyNumberMap {
  const objectKeys = Object.keys(obj)
  return objectKeys.length > 0 && Party.allPartyKeys().includes(objectKeys[0])
}

function isRegionalPoll(obj: any): obj is IRegionalPollMap {
  const objectKeys = Object.keys(obj)
  return objectKeys.length > 0 && Region.allRegionKeys().includes(objectKeys[0])
}

const initialState: IReduxState = {
  baseResultKey: BYELECTIONS_KEY,
  compareResultKey: ELECTION_2019_KEY,
  predictionModel: 'deterministic',
  resultDisplayMode: 'geographicalMap',
  lastReset: new Date().getTime(),
  rawPolls: {},
  outcomes: [],
  outcomesToGenerate: 0,
  mapLocation: {
    // Start in Ottawa
    lat: 62,
    lng: -96,
    zoom: 4
  }
}

const persistConfig = {
  key: 'federal-election-2019',
  storage,
  blacklist: [
    'baseResultKey',
    'compareResultKey',
    'lastReset',
    'rawPolls',
    'outcomes'
  ]
}

const reducer = (
  state: IReduxState = initialState,
  action: IReduxAction
): IReduxState => {
  switch (action.type) {
    case 'SET_PREDICTION_MODEL': {
      const { predictionModel } = action
      return Object.assign({}, state, { predictionModel, outcomes: [] })
    }
    case 'SET_RESULT_DISPLAY_MODE': {
      const { resultDisplayMode } = action
      return Object.assign({}, state, { resultDisplayMode })
    }
    case 'SET_MAP_LOCATION': {
      const { mapLocation } = action
      return Object.assign({}, state, { mapLocation })
    }
    case 'SET_BASE_RESULT_KEY': {
      const { baseResultKey } = action
      // If the result to compare is the base result, also update the result to
      // compare
      console.log('---')
      console.log(
        'baseResultKey',
        baseResultKey,
        'state.baseResultKey',
        processResultKey(state.baseResultKey),
        'state.compareResultKey',
        processResultKey(state.compareResultKey)
      )

      const compareResultKey = compareResultKeys(
        state.baseResultKey,
        state.compareResultKey
      )
        ? baseResultKey
        : state.compareResultKey

      console.log('compareResultKey', compareResultKey)

      return Object.assign({}, state, {
        baseResultKey,
        compareResultKey,
        outcomes: [],
        rawPolls: {}
      })
    }
    case 'SET_COMPARE_RESULT_KEY': {
      const { compareResultKey } = action

      console.log('SET COMPARE RESULT KEY', compareResultKey)

      if (!compareResultKey) throw new Error('SET_COMPARE_RESULT_KEY: No key')

      console.log(resultForKey(compareResultKey))

      if (!resultForKey(compareResultKey)) {
        return state
      }

      return Object.assign({}, state, {
        compareResultKey,
        outcomes: [],
        rawPolls: {}
      })
    }
    case 'PROJECT_FROM_POLLS': {
      const { polls } = action

      if (!polls) throw new Error('PROJECT_FROM_POLLS: No polls.')
      if (!isNationalPoll(polls))
        throw new Error('PROJECT_FROM_POLLS: Not a national poll.')

      const regionalPolls = nationalPollsToRegionalPolls(
        polls,
        state.baseResultKey,
        state.predictionModel
      )

      const newResultKey = generateResultKey('polls')
      const nationalResult = swingFromRegionalPolls(
        regionalPolls,
        state.baseResultKey,
        'deterministic' // Deterministic now because we randomized the diffs earlier
      )
      saveResult(newResultKey, nationalResult)

      return Object.assign({}, state, {
        compareResultKey: newResultKey,
        rawPolls: polls,
        outcomes: []
        // outcomes: [nationalResult.partyResultMap]
      })
    }
    case 'PROJECT_FROM_REGIONAL_POLLS': {
      const { polls } = action

      if (!polls) throw new Error('PROJECT_FROM_REGIONAL_POLLS: No polls.')
      if (!isRegionalPoll(polls))
        throw new Error('PROJECT_FROM_REGIONAL_POLLS: Not a regional poll.')

      const newResultKey = generateResultKey('polls')
      const nationalResult = swingFromRegionalPolls(
        polls,
        state.baseResultKey,
        state.predictionModel,
        0.05
      )
      saveResult(newResultKey, nationalResult)

      return Object.assign({}, state, {
        compareResultKey: newResultKey,
        rawPolls: polls,
        outcomes: []
        // outcomes: [nationalResult.partyResultMap]
      })
    }
    case 'GENERATE_OUTCOME': {
      let polls = state.rawPolls

      // If polls is empty, set it
      if (Object.keys(polls).length === 0) {
        polls = resultForKey(
          state.compareResultKey
        ).partyResultMap.votePercents()
      }

      // console.log('polls', polls)

      let nationalResult

      if (isRegionalPoll(polls)) {
        // console.log('regionalPoll', polls)
        nationalResult = swingFromRegionalPolls(
          polls,
          state.baseResultKey,
          state.predictionModel,
          0.05
        )
      } else {
        // console.log('nationalPoll', polls)
        const regionalPolls = nationalPollsToRegionalPolls(
          polls,
          state.baseResultKey,
          state.predictionModel
        )
        nationalResult = swingFromRegionalPolls(
          regionalPolls,
          state.baseResultKey,
          'deterministic' // Deterministic now because we randomized the diffs earlier
        )
      }

      const outcome = nationalResult.partyResultMap

      const outcomes = state.outcomes.slice()
      outcomes.splice(outcomes.length, 0, outcome)

      return Object.assign({}, state, { outcomes })
    }
    case 'RESET_OUTCOMES': {
      return Object.assign({}, state, { outcomes: [] })
    }
    case 'REPROJECT': {
      const newBaseKey = generateResultKey(state.baseResultKey)
      const newCompareKey = generateResultKey(state.compareResultKey)

      // const nationalResult = resultForKey(state.compareResultKey)

      console.log('REPROJECT', newBaseKey, newCompareKey)
      // saveResult(newCompareKey, nationalResult)

      return Object.assign({}, state, {
        baseResultKey: newBaseKey,
        compareResultKey: newCompareKey
      })
    }
    case 'INITIALIZED':
    case 'RESET': {
      console.log('INIT/RESET')
      DataController.instance().reset()
      return Object.assign({}, state, {
        rawPolls: {},
        lastReset: new Date().getTime(),
        baseResultKey: generateResultKey(BYELECTIONS_KEY),
        compareResultKey: generateResultKey(ELECTION_2019_KEY),
        outcomesToGenerate: 0,
        outcomes: []
      })
    }
    default:
      return state
  }
}

const persistedReducer = persistReducer(persistConfig, reducer)

export const store: Store = createStore(persistedReducer)

const persistor = persistStore(store)

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={'Loading...'} persistor={persistor}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </PersistGate>
  </Provider>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
